/**
 * Super Admin Event Failures — DLQ for failed domain events.
 *
 * Backend uses `failed_event_id` as the row identifier.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RotateCw, Trash2, Eye, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { formatDateTime } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill, JsonViewer,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import { Select } from '@/components/ui_primitives/Field';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { confirm } from '@/components/ui_primitives/confirm';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending_retry', label: 'Pending retry' },
  { value: 'retrying', label: 'Retrying' },
  { value: 'failed', label: 'Failed' },
];

function statusVariant(s) {
  if (s === 'pending_retry') return 'amber';
  if (s === 'retrying') return 'blue';
  if (s === 'failed') return 'red';
  return 'slate';
}

export default function AdminEventFailuresPage() {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [payload, setPayload] = useState(null);

  const filters = { status: statusFilter || undefined, limit: 200 };
  const failuresQ = useQuery({
    queryKey: qk.adminEventFailures(filters),
    queryFn: () => api.adminEventFailures(token, filters),
    enabled: !!token,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-event-failures'] });
  const retry = useMutation({
    mutationFn: (fid) => api.adminRetryEventFailure(token, fid),
    onSuccess: () => { toast.success('Queued for immediate retry'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Retry failed')),
  });
  const discard = useMutation({
    mutationFn: (fid) => api.adminDiscardEventFailure(token, fid),
    onSuccess: () => { toast.success('Failure discarded'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Discard failed')),
  });

  if (failuresQ.isLoading) return <FullSpinner />;
  if (failuresQ.error) return <QueryError error={failuresQ.error} onRetry={() => failuresQ.refetch()} />;
  const items = failuresQ.data || [];

  return (
    <div className="space-y-6" data-testid="admin-event-failures-page">
      <PageHeader
        kicker="Operations"
        title="Failed domain events"
        subtitle="Dead-letter queue for asynchronous handlers. Retry to re-process or discard if no longer relevant."
        actions={
          <Button variant="subtle" leftIcon={<RefreshCcw size={14} />} onClick={() => failuresQ.refetch()} testID="failures-refresh">
            Refresh
          </Button>
        }
      />

      <SectionCard padded>
        <div className="flex items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-gold/80 font-semibold">Status</span>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} testID="failures-status" className="w-48">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </div>
          <div className="text-[11px] text-white/40">
            {items.length} record{items.length === 1 ? '' : 's'}
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={<AlertTriangle size={20} />}
            title="No failures in the queue"
            subtitle="All asynchronous handlers are healthy. 🟢"
          />
        ) : (
          <Table testID="failures-table">
            <THead>
              <Tr>
                <Th>Event</Th>
                <Th>Handler</Th>
                <Th>Status</Th>
                <Th>Attempts</Th>
                <Th>Last error</Th>
                <Th>Last retry</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {items.map((f) => (
                <Tr key={f.failed_event_id}>
                  <Td>
                    <div className="font-mono text-xs">{f.event_type}</div>
                    <div className="text-[11px] text-white/40 font-mono">{(f.failed_event_id || '').slice(0, 14)}…</div>
                  </Td>
                  <Td><span className="font-mono text-xs text-white/80">{f.handler_name}</span></Td>
                  <Td><StatusPill variant={statusVariant(f.status)}>{f.status}</StatusPill></Td>
                  <Td><span className="font-semibold">{f.attempt_count ?? 0}</span></Td>
                  <Td>
                    <div className="text-xs text-red-300/80 max-w-xs truncate" title={f.last_error}>
                      {f.last_error || '—'}
                    </div>
                  </Td>
                  <Td><span className="text-xs text-white/55">{f.last_retry_at ? formatDateTime(f.last_retry_at) : '—'}</span></Td>
                  <Td align="right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="subtle" leftIcon={<Eye size={12} />} onClick={() => setPayload(f)} testID={`failure-view-${f.failed_event_id}`}>View</Button>
                      <Button size="sm" variant="secondary" leftIcon={<RotateCw size={12} />}
                        loading={retry.isPending && retry.variables === f.failed_event_id}
                        onClick={() => retry.mutate(f.failed_event_id)}
                        testID={`failure-retry-${f.failed_event_id}`}>
                        Retry
                      </Button>
                      <Button size="sm" variant="danger" leftIcon={<Trash2 size={12} />}
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Discard this failure?',
                            message: 'The event will not be retried and will remain in the failure log for audit.',
                            confirmText: 'Discard', tone: 'danger',
                          });
                          if (ok) discard.mutate(f.failed_event_id);
                        }}
                        testID={`failure-discard-${f.failed_event_id}`}>
                        Discard
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </SectionCard>

      {payload ? (
        <Modal open onClose={() => setPayload(null)} size="lg"
          title={payload.event_type}
          subtitle={`Handler: ${payload.handler_name}`}
          testID="failure-payload-modal"
          footer={<Button variant="ghost" onClick={() => setPayload(null)}>Close</Button>}
        >
          <div className="space-y-4 text-xs">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gold/80 font-semibold mb-1">Last error</div>
              <div className="text-red-300/90 font-mono bg-red-500/5 border border-red-500/20 rounded-lg p-3 whitespace-pre-wrap">
                {payload.last_error || '—'}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gold/80 font-semibold mb-1">Payload</div>
              <JsonViewer data={payload.payload ?? payload.event_payload ?? {}} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gold/80 font-semibold mb-1">Full record</div>
              <JsonViewer data={payload} />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
